import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { trainerApi, type AttendanceEntryRow, type StudentSummary } from '@/lib/api/trainer';
import { queryKeys } from '@/lib/queryKeys';

type AttendanceStatus = 'present' | 'absent' | 'late';

const STATUS_LABELS: Record<AttendanceStatus, string> = {
  present: 'Present',
  late: 'Late',
  absent: 'Absent',
};

const STATUS_COLORS: Record<AttendanceStatus, string> = {
  present: 'bg-green-100 text-green-800 ring-green-300',
  late: 'bg-amber-100 text-amber-800 ring-amber-300',
  absent: 'bg-red-100 text-red-800 ring-red-300',
};

interface AttendanceSheetProps {
  sessionId: string;
  sessionTitle: string;
  sessionTime: string;
  students: StudentSummary[];
  open: boolean;
  onClose: () => void;
  batchId: string;
}

export function AttendanceSheet({
  sessionId,
  sessionTitle,
  sessionTime,
  students,
  open,
  onClose,
  batchId,
}: AttendanceSheetProps) {
  const queryClient = useQueryClient();
  const [attendance, setAttendance] = useState<Record<string, AttendanceStatus>>({});
  const [loaded, setLoaded] = useState(false);

  // Pre-load existing attendance when sheet opens
  useEffect(() => {
    if (!open) return;
    // Default everyone to present
    const defaults: Record<string, AttendanceStatus> = {};
    for (const s of students) defaults[s.id] = 'present';
    setAttendance(defaults);
    setLoaded(false);

    trainerApi.getAttendance(sessionId).then(({ data }) => {
      if (data && data.length > 0) {
        const loaded: Record<string, AttendanceStatus> = { ...defaults };
        for (const row of data) loaded[row.user_id] = row.status;
        setAttendance(loaded);
      }
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [open, sessionId, students]);

  const mutation = useMutation({
    mutationFn: () =>
      trainerApi.markAttendance(sessionId, {
        attendance: Object.entries(attendance).map(([user_id, status]) => ({ user_id, status })),
      }),
    onSuccess: () => {
      toast.success('Attendance saved');
      queryClient.invalidateQueries({ queryKey: queryKeys.trainer.batchOverview(batchId) });
      onClose();
    },
    onError: () => toast.error('Failed to save attendance'),
  });

  const toggle = (userId: string, status: AttendanceStatus) =>
    setAttendance((prev) => ({ ...prev, [userId]: status }));

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      {/* Panel */}
      <div className="relative flex h-full w-full max-w-md flex-col bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-[#ececec] px-5 py-4">
          <div>
            <p className="text-sm font-semibold text-[#111827]">{sessionTitle}</p>
            <p className="mt-0.5 text-xs text-[#6b7280]">{sessionTime}</p>
          </div>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-[#fafafa]">
            <X className="h-4 w-4 text-[#6b7280]" />
          </button>
        </div>

        {/* Roster */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!loaded ? (
            <div className="flex h-32 items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#ff5a1f] border-t-transparent" />
            </div>
          ) : (
            <div className="space-y-2">
              {students.map((s) => {
                const current = attendance[s.id] ?? 'present';
                return (
                  <div key={s.id} className="flex items-center gap-3 rounded-lg border border-[#ececec] bg-white px-3 py-2.5">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#f3f4f6] text-xs font-semibold text-[#6b7280]">
                      {s.username.charAt(0).toUpperCase()}
                    </div>
                    <span className="flex-1 text-sm font-medium text-[#111827]">{s.username}</span>
                    <div className="flex gap-1">
                      {(['present', 'late', 'absent'] as AttendanceStatus[]).map((status) => (
                        <button
                          key={status}
                          onClick={() => toggle(s.id, status)}
                          className={`rounded-full px-2.5 py-1 text-xs font-medium ring-1 transition-all ${
                            current === status
                              ? STATUS_COLORS[status]
                              : 'bg-[#f3f4f6] text-[#6b7280] ring-transparent hover:ring-border'
                          }`}
                        >
                          {STATUS_LABELS[status]}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-[#ececec] px-5 py-4">
          <button
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-[#6b7280] hover:bg-[#fafafa]"
          >
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !loaded}
            className="rounded-lg bg-[#ff5a1f] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving…' : 'Save Attendance'}
          </button>
        </div>
      </div>
    </div>
  );
}
